/**
 * CARRITOS GUARDADOS — "guárdame esto y atiendo al siguiente".
 *
 * ═══════════════════════════════════════════════════════════════════════════
 *  EL CASO REAL
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * El cliente está haciendo la transferencia y el banco tarda. Detrás hay cola.
 * El cajero no puede quedarse esperando ni perder el carrito que ya escaneó:
 * lo guarda, atiende al siguiente, y cuando el cliente confirma lo retoma.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 *  AL RETOMARLO, LOS PRECIOS SE RECALCULAN
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Lo guardado son IDs y cantidades, nunca precios. Al retomar la venta, cada
 * producto se vuelve a leer del catálogo que ya está en el dispositivo.
 *
 * Es lo correcto: si en esos diez minutos el dueño actualizó la tasa del día,
 * hay que cobrar la tasa buena. Un carrito guardado todavía no es una venta,
 * así que no congela nada.
 *
 * ⚠️ La consecuencia que hay que avisar: si un producto se borró del catálogo
 * mientras esperaba, esa línea no se puede recuperar. Se dice claramente en vez
 * de recuperar la venta a medias sin que nadie se entere.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 *  ⚠️ VUELVE LA CUENTA ENTERA, NO SOLO LOS PRODUCTOS
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Lo encontró la auditoría. Al retomar volvían las líneas y la nota, y nada
 * más: la cuenta del cliente de «Mayor» regresaba con precios normales, sin su
 * cliente, sin el IGTF que se había encendido y sin el descuento general. Las
 * recargas volvían sin su monto, y así no se podían cobrar.
 *
 * Nada de eso daba error: se cobraba otra cosa, en silencio, con el cliente
 * delante. Ahora se guarda todo lo que se había elegido —cuál lista, cuáles
 * impuestos, nunca cuánto— y vuelve igual.
 */

import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Clock, PauseCircle, Trash2, X } from "lucide-react";
import { Boton, Campo, Etiquetado, Insignia, Nota, Vacio, cx } from "@/components/ui";
import { Confirmar, Modal } from "@/components/Modal";
import { mensajeDeError } from "@/lib/api";
import { leerClienteLocal } from "@/lib/db";
import { carritos, listasDePrecios } from "@/lib/endpoints";
import type { CarritoGuardado, ItemListRow, Tax } from "@/lib/types";
import { claveDeLinea, useCarrito } from "@/stores/carrito";
import { aviso } from "@/stores/ui";

// =============================================================================
//  Botón para guardar el carrito actual
// =============================================================================

export function BotonGuardarCarrito({ deshabilitado }: { deshabilitado?: boolean }) {
  const qc = useQueryClient();
  const carrito = useCarrito();
  const [abierto, setAbierto] = useState(false);
  const [etiqueta, setEtiqueta] = useState("");
  const [error, setError] = useState<string | null>(null);

  const guardar = useMutation({
    mutationFn: () =>
      carritos.guardar({
        etiqueta: etiqueta.trim(),
        nota: carrito.nota || null,
        customer_id: carrito.clienteId,
        lineas: carrito.lineas.map((l) => ({
          item_id: l.itemId,
          variant_id: l.variantId,
          qty: String(l.cantidad),
          discount: String(l.descuento),
          staff_user_id: l.staffUserId,
          monto: l.monto != null ? String(l.monto) : null,
          variant_name: l.varianteNombre,
          staff_nombre: l.staffNombre,
          opciones: l.opciones?.length ? l.opciones : undefined,
        })),
        ajustes: {
          cliente_nombre: carrito.clienteId ? carrito.clienteNombre : null,
          lista_precios_id: carrito.listaPreciosId,
          lista_precios_nombre: carrito.listaPreciosNombre,
          order_tax_ids: carrito.impuestosPedido,
          discount_total: String(carrito.descuentoGlobal),
        },
      }),
    onSuccess: (v) => {
      aviso.exito("Cuenta en espera", `La retomas desde "Cuentas en espera" como «${v.etiqueta}».`);
      carrito.vaciar();
      setEtiqueta("");
      setAbierto(false);
      void qc.invalidateQueries({ queryKey: ["carritos-guardados"] });
    },
    onError: (e) => setError(mensajeDeError(e)),
  });

  return (
    <>
      <Boton
        variante="secundario"
        ancho
        icono={<PauseCircle size={16} />}
        disabled={deshabilitado}
        onClick={() => setAbierto(true)}
      >
        Dejar en espera
      </Boton>

      {abierto && (
        <Modal
          abierto
          onCerrar={() => setAbierto(false)}
          titulo="Dejar esta cuenta en espera"
          descripcion="Atiende al siguiente cliente y retómala cuando este vuelva. No se cobra nada todavía."
          ancho="sm"
          pie={
            <>
              <Boton variante="secundario" onClick={() => setAbierto(false)}>
                Cancelar
              </Boton>
              <Boton
                onClick={() => {
                  setError(null);
                  guardar.mutate();
                }}
                cargando={guardar.isPending}
                disabled={etiqueta.trim().length === 0}
              >
                Dejar en espera
              </Boton>
            </>
          }
        >
          <div className="space-y-4">
            <Etiquetado
              label="¿Cómo la reconoces?"
              ayuda="Algo que te sirva a ti para encontrarla: cómo va vestido, qué lleva, su nombre."
              requerido
            >
              <Campo
                value={etiqueta}
                onChange={(e) => setEtiqueta(e.target.value)}
                placeholder="Señora del abrigo rojo"
                maxLength={80}
                autoFocus
                onKeyDown={(e) => {
                  if (e.key === "Enter" && etiqueta.trim()) guardar.mutate();
                }}
              />
            </Etiquetado>

            <Nota tono="neutro">
              Al retomarla, los precios se vuelven a calcular con la{" "}
              <strong>tasa del momento</strong>. Si actualizas la tasa mientras espera, se cobra
              la buena.
            </Nota>

            {error && <Nota tono="peligro">{error}</Nota>}
          </div>
        </Modal>
      )}
    </>
  );
}

// =============================================================================
//  Lista de carritos guardados
// =============================================================================

export function ListaDeCarritos({
  items,
  impuestosDePedido,
}: {
  items: ItemListRow[];
  /** Los impuestos de pedido que la caja enseña ahora mismo (vacío si están apagados). */
  impuestosDePedido: Tax[];
}) {
  const qc = useQueryClient();
  const [abierto, setAbierto] = useState(false);
  const [retomando, setRetomando] = useState<string | null>(null);
  const [aDescartar, setADescartar] = useState<CarritoGuardado | null>(null);

  const { data: pendientes } = useQuery({
    queryKey: ["carritos-guardados"],
    queryFn: carritos.listar,
    // Cada minuto: si otro cajero guarda un carrito en la caja de al lado,
    // aparece sin tener que recargar.
    refetchInterval: 60_000,
  });

  const descartar = useMutation({
    mutationFn: (id: string) => carritos.descartar(id),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["carritos-guardados"] }),
    onError: (e) => aviso.error(mensajeDeError(e)),
  });

  const cuantas = pendientes?.length ?? 0;
  if (cuantas === 0) return null;

  /**
   * Retoma un carrito: reconstruye el carrito leyendo los precios de HOY.
   */
  async function retomar(v: CarritoGuardado) {
    if (useCarrito.getState().lineas.length > 0) {
      avisarCuentaAMedias();
      return;
    }
    if (retomando) return;
    setRetomando(v.id);

    try {
      const ajustes = v.ajustes ?? null;
      const avisos: string[] = [];

      /*
        La lista, solo si SIGUE ACTIVA. Una lista apagada mientras la cuenta
        esperaba haría que el cobro fallara; mejor decirlo ahora y que vuelva
        con los precios normales. Si no se puede comprobar (sin señal), se deja
        la guardada: al cobrar, el servidor la valida igual.
      */
      let lista: { id: string; nombre: string | null } | null = ajustes?.lista_precios_id
        ? { id: ajustes.lista_precios_id, nombre: ajustes.lista_precios_nombre }
        : null;
      if (lista) {
        const buscada = lista;
        try {
          const activas = await qc.fetchQuery({
            queryKey: ["listas-de-precios", "activas"],
            queryFn: () => listasDePrecios.listar(true),
            staleTime: 60_000,
          });
          const sigue = activas.find((l) => l.id === buscada.id);
          if (sigue) {
            lista = { id: sigue.id, nombre: sigue.nombre };
          } else {
            avisos.push(
              `La lista «${buscada.nombre ?? "de precios"}» ya no está activa: vuelve con los precios normales.`,
            );
            lista = null;
          }
        } catch {
          // Sin comprobar: se queda la guardada.
        }
      }

      /*
        El cliente, solo si se sabe su nombre. Con el id y sin nombre, el panel
        no lo enseñaría pero la venta sí lo llevaría: un cliente invisible. Las
        guardadas antes de este cambio no traen el nombre; se busca en la copia
        del aparato.
      */
      let clienteNombre = ajustes?.cliente_nombre ?? null;
      if (v.customer_id && !clienteNombre) {
        const local = await leerClienteLocal(v.customer_id).catch(() => undefined);
        clienteNombre = local?.name ?? null;
      }

      // Lo que pide esperar ya pasó. Si mientras tanto alguien empezó a meter
      // productos, no se mezcla una cuenta con otra.
      if (useCarrito.getState().lineas.length > 0) {
        avisarCuentaAMedias();
        return;
      }

      const c = useCarrito.getState();
      let perdidas = 0;
      for (const l of v.lineas) {
        const item = items.find((i) => i.id === l.item_id);
        if (!item) {
          // El producto se borró del catálogo mientras esperaba.
          perdidas++;
          continue;
        }
        const monto = l.monto != null ? Number(l.monto) : null;
        c.agregar(
          item,
          l.variant_id,
          l.variant_name ?? null,
          Number(l.qty),
          monto ?? undefined,
          l.opciones,
        );
        const key = claveDeLinea(item.id, l.variant_id, monto, l.opciones);
        if (Number(l.discount) > 0) c.ponerDescuentoLinea(key, Number(l.discount));
        if (l.staff_user_id) c.ponerPersonal(key, l.staff_user_id, l.staff_nombre ?? null);
      }

      if (v.nota) c.ponerNota(v.nota);
      if (v.customer_id && clienteNombre) c.ponerCliente(v.customer_id, clienteNombre);
      if (lista) c.ponerListaDePrecios(lista.id, lista.nombre);
      if (ajustes && Number(ajustes.discount_total) > 0) {
        c.ponerDescuentoGlobal(Number(ajustes.discount_total));
      }

      /*
        Los impuestos, solo los que la caja enseña ahora. Uno apagado mientras
        esperaba quedaría encendido sin interruptor para apagarlo, y el cobro
        fallaría con «está desactivado».
      */
      const guardados = ajustes?.order_tax_ids ?? [];
      const disponibles = new Set(impuestosDePedido.map((t) => t.id));
      const siguen = guardados.filter((id) => disponibles.has(id));
      c.ponerImpuestosDePedido(siguen);
      if (siguen.length < guardados.length) {
        avisos.push("Un impuesto que estaba encendido ya no se usa: revísalo antes de cobrar.");
      }

      descartar.mutate(v.id);
      setAbierto(false);

      if (perdidas > 0) {
        aviso.error(
          `${perdidas} ${perdidas === 1 ? "producto ya no existe" : "productos ya no existen"}`,
          "Se borraron del catálogo mientras la cuenta esperaba. Revísala antes de cobrar.",
        );
      } else if (avisos.length > 0) {
        aviso.error(`Cuenta de «${v.etiqueta}» retomada, con cambios`, avisos.join(" "));
      } else {
        aviso.exito(`Cuenta de «${v.etiqueta}» retomada`, "Los precios son los de ahora.");
      }
    } finally {
      setRetomando(null);
    }
  }

  return (
    <>
      <button
        type="button"
        onClick={() => setAbierto(true)}
        className={cx(
          "flex w-full items-center gap-2.5 rounded-[var(--radius-control)] border px-3 py-2.5",
          "border-warning/40 bg-warning-soft text-sm font-medium text-warning",
          "transition-colors hover:brightness-105",
        )}
      >
        <Clock size={16} className="shrink-0" />
        <span className="flex-1 text-left">
          {/* Con mayúscula: funciona como el nombre de la sección, no como una
              frase suelta dentro de un párrafo. */}
          {cuantas === 1 ? "1 Cuenta en espera" : `${cuantas} Cuentas en espera`}
        </span>
        <Insignia tono="aviso">Ver</Insignia>
      </button>

      {abierto && (
        <Modal
          abierto
          onCerrar={() => setAbierto(false)}
          titulo="Cuentas en espera"
          descripcion="Toca uno para retomarlo. Los precios se recalculan con la tasa de ahora."
          ancho="sm"
          pie={
            <Boton variante="secundario" onClick={() => setAbierto(false)}>
              Cerrar
            </Boton>
          }
        >
          {cuantas === 0 ? (
            <Vacio icono={<Clock size={22} />} titulo="No hay cuentas en espera" />
          ) : (
            <div className="divide-y divide-line">
              {pendientes!.map((v) => (
                <div key={v.id} className="flex items-center gap-3 py-3">
                  <button
                    type="button"
                    onClick={() => void retomar(v)}
                    disabled={retomando !== null}
                    className="min-w-0 flex-1 text-left"
                  >
                    <p className="truncate font-medium text-ink">{v.etiqueta}</p>
                    <p className="mt-0.5 truncate text-xs text-muted">
                      {v.lineas.length}{" "}
                      {v.lineas.length === 1 ? "producto" : "productos"} ·{" "}
                      {hace(v.created_at)}
                      {/* Quién es, si se sabe: ayuda a encontrar la suya. */}
                      {v.ajustes?.cliente_nombre && <> · {v.ajustes.cliente_nombre}</>}
                    </p>
                  </button>

                  <Boton
                    tamano="sm"
                    onClick={() => void retomar(v)}
                    cargando={retomando === v.id}
                    disabled={retomando !== null && retomando !== v.id}
                  >
                    Retomar
                  </Boton>

                  <button
                    type="button"
                    onClick={() => setADescartar(v)}
                    aria-label={`Descartar ${v.etiqueta}`}
                    className="shrink-0 rounded-[var(--radius-control)] p-2 text-faint transition-colors hover:bg-danger-soft hover:text-danger"
                  >
                    <Trash2 size={15} />
                  </button>
                </div>
              ))}
            </div>
          )}
        </Modal>
      )}

      <Confirmar
        abierto={aDescartar !== null}
        onCerrar={() => setADescartar(null)}
        onConfirmar={() => {
          if (aDescartar) descartar.mutate(aDescartar.id);
          setADescartar(null);
        }}
        cargando={descartar.isPending}
        peligroso
        titulo={`¿Descartar la cuenta de «${aDescartar?.etiqueta ?? ""}»?`}
        textoConfirmar="Sí, descartar"
        mensaje="No se cobró nada, así que no se pierde ninguna venta: solo se borra lo que estaba apuntado."
      />
    </>
  );
}

function avisarCuentaAMedias() {
  aviso.error("Tienes una cuenta a medias", "Cóbrala o déjala en espera antes de retomar otra.");
}

/** "hace 5 minutos", "hace 2 horas". */
function hace(iso: string): string {
  const minutos = Math.floor((Date.now() - new Date(iso).getTime()) / 60000);
  if (minutos < 1) return "hace un momento";
  if (minutos < 60) return `hace ${minutos} min`;
  const horas = Math.floor(minutos / 60);
  if (horas < 24) return `hace ${horas} h`;
  return `hace ${Math.floor(horas / 24)} d`;
}

/** Se exporta para que el POS pueda cerrar el modal desde fuera si hiciera falta. */
export { X as IconoCerrar };
